// Fuel Consumption & CO2 Converter tool: modes, unit/fuel metadata, grid presets and
// client-side field validation, used for instant inline feedback as the user types. The
// actual conversion math, CO2 factors and the authoritative (server-side) validation
// live in the main backend — see src/lib/toolsApi.js (fuelConsumptionToolApi) and
// backend/tools/fuel_consumption_tool.py.

export const DEFAULT_INPUTS = {
  mode: 'fuel',
  fuel: 'petrol_e0',
  fuelUnit: 'l_per_100km',
  fuelValue: 6.0,
  electricUnit: 'kwh_per_100km',
  electricValue: 15,
  gridPreset: 'india',
  gridIntensity: 727,
};

export const MODES = [
  { id: 'fuel', label: 'Fuel (Liquid / Gas)' },
  { id: 'electric', label: 'Electric' },
];

// `basis` is the fuel quantity the CO2 factor is per: liquid fuels use the litre units,
// CNG uses the mass units. Factors themselves come back from the backend.
// Mirrors FUELS in backend/core/lookup/fuel_consumption_specs.py.
export const FUELS = [
  { value: 'petrol_e0', label: 'Petrol (E0)', basis: 'L' },
  { value: 'petrol_e10', label: 'Petrol E10', basis: 'L' },
  { value: 'petrol_e20', label: 'Petrol E20', basis: 'L' },
  { value: 'diesel', label: 'Diesel (B0/B7)', basis: 'L' },
  { value: 'lpg', label: 'LPG', basis: 'L' },
  { value: 'cng', label: 'CNG', basis: 'kg' },
];

// First entry of each family is its canonical unit. Order drives display order.
export const LIQUID_UNITS = [
  { value: 'l_per_100km', label: 'L/100 km', name: 'Litres per 100 km' },
  { value: 'km_per_l', label: 'km/L', name: 'Kilometres per litre' },
  { value: 'mpg_us', label: 'mpg (US)', name: 'Miles per US gallon' },
  { value: 'mpg_uk', label: 'mpg (UK)', name: 'Miles per imperial gallon' },
];
export const CNG_UNITS = [
  { value: 'kg_per_100km', label: 'kg/100 km', name: 'Kilograms per 100 km' },
  { value: 'km_per_kg', label: 'km/kg', name: 'Kilometres per kilogram' },
];
export const ELECTRIC_UNITS = [
  { value: 'kwh_per_100km', label: 'kWh/100 km', name: 'Kilowatt-hours per 100 km' },
  { value: 'wh_per_km', label: 'Wh/km', name: 'Watt-hours per kilometre' },
  { value: 'km_per_kwh', label: 'km/kWh', name: 'Kilometres per kilowatt-hour' },
  { value: 'mi_per_kwh', label: 'mi/kWh', name: 'Miles per kilowatt-hour' },
];

export function unitsFor(mode, fuel) {
  if (mode === 'electric') return ELECTRIC_UNITS;
  const info = FUELS.find((f) => f.value === fuel);
  return info && info.basis === 'kg' ? CNG_UNITS : LIQUID_UNITS;
}

// Exact definitions — mirrors backend/core/lookup/fuel_consumption_specs.py.
const US_GALLON_L = 3.785411784;
const IMPERIAL_GALLON_L = 4.54609;
const MILE_KM = 1.609344;

// Reciprocal relations (K / v) are self-inverse, so one table serves both directions
// for everything except Wh/km, which is plain multiplication.
const RECIPROCAL_K = {
  km_per_l: 100,
  mpg_us: (100 * US_GALLON_L) / MILE_KM,
  mpg_uk: (100 * IMPERIAL_GALLON_L) / MILE_KM,
  km_per_kg: 100,
  km_per_kwh: 100,
  mi_per_kwh: 100 / MILE_KM,
};

function toCanonical(unit, v) {
  if (unit === 'wh_per_km') return v / 10;
  if (RECIPROCAL_K[unit] !== undefined) return RECIPROCAL_K[unit] / v;
  return v;
}

function fromCanonical(unit, x) {
  if (unit === 'wh_per_km') return x * 10;
  return toCanonical(unit, x);
}

// Valid range in each family's canonical unit (mirrors CANONICAL_RANGE).
export const CANONICAL_RANGE = {
  l_per_100km: [1, 100],
  kg_per_100km: [1, 100],
  kwh_per_100km: [1, 200],
};

const RANGE_TOLERANCE = 1e-12;

const fmt = (n) => n.toFixed(3).replace(/\.?0+$/, '');

// The canonical range expressed in `unit`. Reciprocal units flip the bounds, so min is
// rounded up and max down (3 dp) — anything inside the displayed range passes.
export function rangeInUnit(units, unit) {
  const canonicalUnit = units[0].value;
  const [lo, hi] = CANONICAL_RANGE[canonicalUnit];
  const a = fromCanonical(unit, lo);
  const b = fromCanonical(unit, hi);
  return {
    canonicalUnit, lo, hi,
    min: Math.ceil(Math.min(a, b) * 1000 - 1e-9) / 1000,
    max: Math.floor(Math.max(a, b) * 1000 + 1e-9) / 1000,
  };
}

export const GRID_PRESETS = [
  { value: 'india', label: 'India', gPerKwh: 727, source: 'CEA CO₂ Baseline Database v20.0, FY 2023-24' },
  { value: 'eu', label: 'EU-27', gPerKwh: 242, source: 'Ember European Electricity Review 2024 (2023 data)' },
  { value: 'usa', label: 'USA', gPerKwh: 348, source: 'US EPA eGRID2023 (767.209 lb/MWh)' },
];

export const GRID_INTENSITY_SPEC = { key: 'gridIntensity', label: 'Grid intensity', min: 0, max: 2000 };

export const PRECISION_OPTIONS = [2, 3, 4, 5, 6];
export const DEFAULT_PRECISION = 2;

// `units` is the selected family (unitsFor()) and `unit` the selected unit — the valid
// range depends on both, so unlike most converters' validateField(spec, rawValue) this
// needs the unit context. Mirrors convert() in backend/tools/fuel_consumption_tool.py.
export function validateValue(rawValue, units, unit) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return 'Required.';
  const value = Number(rawValue);
  if (Number.isNaN(value)) return 'Must be a number.';
  if (value <= 0) return 'Must be greater than 0.';
  const r = rangeInUnit(units, unit);
  const canonical = toCanonical(unit, value);
  if (canonical < r.lo * (1 - RANGE_TOLERANCE) || canonical > r.hi * (1 + RANGE_TOLERANCE)) {
    const label = (units.find((u) => u.value === unit) || units[0]).label;
    return `Must be between ${fmt(r.min)} and ${fmt(r.max)} ${label} (${fmt(r.lo)}–${fmt(r.hi)} ${units[0].label}).`;
  }
  return null;
}

// Grid intensity is optional: blank means "no CO2 output", not an error.
export function validateGridIntensity(rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return null;
  const value = Number(rawValue);
  if (Number.isNaN(value)) return 'Must be a number.';
  if (value < GRID_INTENSITY_SPEC.min) return `Must be ≥ ${GRID_INTENSITY_SPEC.min}.`;
  if (value > GRID_INTENSITY_SPEC.max) return `Must be ≤ ${GRID_INTENSITY_SPEC.max}.`;
  return null;
}
